import { apiClient } from '@/lib/api/client'

export interface AnalyticsOverview {
  summary: {
    total_users: number
    total_pages: number
    total_media: number
    total_categories: number
    total_tags: number
  }
  user_metrics: {
    total: number
    active: number
    new_today: number
    new_this_week: number
    new_this_month: number
    new_last_30_days: number
  }
  content_metrics: {
    total_pages: number
    published_pages: number
    draft_pages: number
    pages_this_month: number
  }
  activity_metrics: {
    total: number
    today: number
    this_week: number
    by_module: Record<string, number>
    by_action: Record<string, number>
  }
  trends: {
    user_registration: {
      labels: string[]
      data: number[]
    }
    page_creation: {
      labels: string[]
      data: number[]
    }
  }
}

/** Tickets overview filters */
export interface TicketsOverviewParams {
  category_id?: number | null
  ticket_status_id?: number | null
  date_from?: string
  date_to?: string
  department_name?: string | null
  statistics_type?: 'tickets' | 'agents' | 'mttr'
}

/** Ticket detail row (statistics_type = tickets): ticket request + status + SLA clock */
export interface TicketsOverviewRowDetail {
  id: number
  request_number: string
  user_id: number | null
  created_by: number | null
  created_by_name: string | null
  service_type_id: number | null
  service_type_name: string | null
  service_category: string | null
  category_name: string | null
  sub_category_name: string | null
  item_name: string | null
  description: string | null
  contact_name: string | null
  contact_email: string | null
  contact_number: string | null
  location?: string | null
  ticket_status_id: number | null
  status_code: string | null
  status_label: string | null
  /** Division label from ticket (text); codes come from API when catalog matches */
  division?: string | null
  division_code?: string | null
  department_code?: string | null
  department?: { id?: number; code?: string | null; name?: string | null } | null
  ticket_priority_id?: number | null
  ticket_priority_label?: string | null
  priority?: string | null
  ticket_priority?: { id?: number | null; label?: string | null; level?: number | null } | null
  slas_id?: number | null
  sla_severity_label?: string | null
  severity?: string | null
  sla?: { id?: number | null; severity?: string | null } | null
  assigned_to: string | null
  submitted_at: string | null
  resolved_at: string | null
  closed_at: string | null
  created_at: string | null
  updated_at: string | null
  sla_clock_id: number | null
  sla_clock_started_at: string | null
  sla_clock_due_at: string | null
  sla_clock_response_due_at: string | null
  sla_clock_status: string | null
  sla_clock_breached_at: string | null
  sla_clock_completed_at: string | null
  sla_breached: boolean | null
  net_recovery_minutes?: number | null
  csat_score?: number | string | null
  csat_score_label?: string | null
  csat_submitted_at?: string | null
}

/** Agent row (statistics_type = agents) */
export interface TicketsOverviewRowAgent {
  agent_id: number
  agent_name: string
  open_count: number
  resolved_count: number
  total_count: number
}

/** MTTR row (statistics_type = mttr) */
export interface TicketsOverviewRowMttr {
  id: number
  request_number: string
  requestor: string | null
  requestor_email: string | null
  slas_id?: number | null
  sla_severity_label?: string | null
  ticket_priority_id?: number | null
  ticket_priority_label?: string | null
  priority: string | null
  severity: string | null
  summary: string | null
  created_at: string | null
  resolved_at: string | null
  sla_clock_started_at: string | null
  sla_clock_completed_at: string | null
  sla_clock_total_paused_minutes?: number | null
  net_recovery_minutes: number | null
  net_recovery_hours: number | null
}

export type TicketsOverviewTableRow =
  | TicketsOverviewRowDetail
  | TicketsOverviewRowAgent
  | TicketsOverviewRowMttr

export interface TicketsOverviewMttrSummary {
  ticket_count: number
  average_mttr_minutes: number
  average_mttr_hours: number
  total_mttr_minutes: number
}

export interface TicketsOverviewResponse {
  chart: { labels: string[]; data: number[] }
  table: TicketsOverviewTableRow[]
  statistics_type: 'tickets' | 'agents' | 'mttr'
  mttr_summary?: TicketsOverviewMttrSummary | null
}

export const analyticsService = {
  async getOverview(): Promise<AnalyticsOverview> {
    const response = await apiClient.get('/analytics/overview')
    return response.data.data
  },

  async getTicketsOverview(params: TicketsOverviewParams = {}): Promise<TicketsOverviewResponse> {
    const searchParams = new URLSearchParams()
    if (params.category_id != null) {
      searchParams.set('category_id', String(params.category_id))
    }
    if (params.ticket_status_id != null && params.ticket_status_id > 0) {
      searchParams.set('ticket_status_id', String(params.ticket_status_id))
    }
    if (params.date_from) searchParams.set('date_from', params.date_from)
    if (params.date_to) searchParams.set('date_to', params.date_to)
    if (params.statistics_type) searchParams.set('statistics_type', params.statistics_type)
    const response = await apiClient.get(
      `/analytics/tickets-overview?${searchParams.toString()}`
    )
    const envelope = response.data?.data ?? response.data
    const { data, mttr_summary } = envelope ?? {}
    const body = data ?? envelope
    return {
      ...(body || {}),
      mttr_summary: mttr_summary ?? envelope?.mttr_summary ?? null,
    } as TicketsOverviewResponse
  },
}

